import React, { useState, useEffect, useRef } from 'react';
import { Plus, Trash2, ChevronUp, ChevronDown, Square, Paperclip, Upload, X, Loader } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';

const ChecklistBuilder = ({ content, onChange, onContentChange }) => {
    const [checklist, setChecklist] = useState({
        items: []
    });

    useEffect(() => {
        try {
            if (content) {
                let parsed = content;
                // Handle string content (from database)
                if (typeof parsed === 'string') {
                    try {
                        parsed = JSON.parse(parsed);
                        // Handle double-stringified JSON
                        if (typeof parsed === 'string') {
                            parsed = JSON.parse(parsed);
                        }
                    } catch (e) {
                        console.error("Failed to parse checklist content:", e);
                        parsed = { items: [] };
                    }
                }

                // Ensure it's an object with items array
                if (typeof parsed === 'object' && parsed !== null) {
                    setChecklist(parsed);
                } else {
                    setChecklist({ items: [] });
                }
            } else {
                setChecklist({ items: [] });
            }
        } catch (e) {
            console.error("Error initializing checklist:", e);
            setChecklist({ items: [] });
        }
    }, [content]);

    const handleChecklistChange = (updatedChecklist) => {
        setChecklist(updatedChecklist);
        onChange(JSON.stringify(updatedChecklist, null, 2));
    };

    const addItem = () => {
        const newItem = {
            id: Date.now(),
            text: '',
            description: '',
            required: true,
            category: '',
            attachments: []
        };

        handleChecklistChange({
            ...checklist,
            items: [...checklist.items, newItem]
        });
        onContentChange?.('checklist_items', `Added new item ${checklist.items.length + 1}`);
    };

    const updateItem = (index, field, value) => {
        const updatedItems = [...checklist.items];
        updatedItems[index] = { ...updatedItems[index], [field]: value };

        handleChecklistChange({ ...checklist, items: updatedItems });

        let valueDisplay = value;
        if (typeof value === 'string' && value.length > 50) {
            valueDisplay = value.substring(0, 50) + '...';
        }
        onContentChange?.('checklist_items', `Updated ${field} for item ${index + 1} to: "${valueDisplay}"`);
    };

    const deleteItem = (index) => {
        const updatedItems = checklist.items.filter((_, idx) => idx !== index);
        handleChecklistChange({ ...checklist, items: updatedItems });
        onContentChange?.('checklist_items', `Deleted item ${index + 1}`);
    };

    const moveItem = (index, direction) => {
        const newIndex = direction === 'up' ? index - 1 : index + 1;
        if (newIndex >= 0 && newIndex < checklist.items.length) {
            const updatedItems = [...checklist.items];
            [updatedItems[index], updatedItems[newIndex]] =
                [updatedItems[newIndex], updatedItems[index]];

            handleChecklistChange({ ...checklist, items: updatedItems });
        }
    };

    // Item file attachment handlers
    const [uploadingItem, setUploadingItem] = useState(null);
    const fileInputRefs = useRef({});

    const handleItemFileUpload = async (itemIndex, files) => {
        if (!files || files.length === 0) return;

        // Validate file size (max 10MB each)
        const invalidFiles = Array.from(files).filter(file => file.size > 10 * 1024 * 1024);
        if (invalidFiles.length > 0) {
            toast.error('Some files exceed the 10 MB limit');
            return;
        }

        setUploadingItem(itemIndex);

        try {
            const formData = new FormData();
            Array.from(files).forEach(file => {
                formData.append('files', file);
            });

            const token = localStorage.getItem('token');
            const response = await api.post('/files/upload', formData, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data',
                },
            });

            if (response.data.success) {
                const updatedItems = [...checklist.items];
                const item = { ...updatedItems[itemIndex] };
                const attachments = item.attachments ? [...item.attachments] : [];

                attachments.push(...response.data.files);
                item.attachments = attachments;
                updatedItems[itemIndex] = item;

                handleChecklistChange({ ...checklist, items: updatedItems });
                toast.success(`${response.data.files.length} file(s) uploaded`);
            }
        } catch (error) {
            console.error('File upload error:', error);
            toast.error(error.response?.data?.message || 'Failed to upload file');
        } finally {
            setUploadingItem(null);
            if (fileInputRefs.current[itemIndex]) {
                fileInputRefs.current[itemIndex].value = '';
            }
        }
    };

    const removeItemAttachment = (itemIndex, attachmentIndex) => {
        const updatedItems = [...checklist.items];
        const item = { ...updatedItems[itemIndex] };
        item.attachments = item.attachments.filter((_, idx) => idx !== attachmentIndex);
        updatedItems[itemIndex] = item;

        handleChecklistChange({ ...checklist, items: updatedItems });
        toast.success('File removed');
    };

    // Group items by category for display
    const categories = [...new Set((checklist.items || []).map(item => item.category || 'General'))];
    const groupedItems = categories.map(category => ({
        category,
        items: (checklist.items || [])
            .map((item, index) => ({ ...item, originalIndex: index }))
            .filter(item => (item.category || 'General') === category)
    }));

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-800 dark:text-slate-100">Checklist Builder</h3>
                <button
                    type="button"
                    onClick={addItem}
                    className="flex items-center gap-2 bg-primary hover:bg-primary-hover text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                >
                    <Plus className="w-4 h-4" />
                    Add Item
                </button>
            </div>

            {(checklist.items || []).length === 0 ? (
                <div className="bg-gray-50 dark:bg-slate-800/50 border-2 border-dashed border-gray-300 dark:border-slate-600 rounded-lg p-8 text-center">
                    <Square className="w-12 h-12 text-gray-400 dark:text-slate-500 mx-auto mb-3" />
                    <p className="text-gray-600 dark:text-slate-300 mb-4">No checklist items yet. Click "Add Item" to get started.</p>
                </div>
            ) : (
                <div className="space-y-4">
                    {checklist.items.map((item, index) => (
                        <div key={item.id || index} className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-lg p-4 shadow-sm">
                            <div className="flex items-start gap-3">
                                {/* Reorder Buttons */}
                                <div className="flex flex-col gap-1">
                                    <button
                                        type="button"
                                        onClick={() => moveItem(index, 'up')}
                                        disabled={index === 0}
                                        className="p-1 text-gray-400 hover:text-gray-600 dark:text-slate-300 disabled:opacity-30 disabled:cursor-not-allowed"
                                    >
                                        <ChevronUp className="w-4 h-4" />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => moveItem(index, 'down')}
                                        disabled={index === checklist.items.length - 1}
                                        className="p-1 text-gray-400 hover:text-gray-600 dark:text-slate-300 disabled:opacity-30 disabled:cursor-not-allowed"
                                    >
                                        <ChevronDown className="w-4 h-4" />
                                    </button>
                                </div>

                                {/* Item Number */}
                                <div className="flex items-center justify-center w-8 h-8 bg-primary/10 rounded-full text-primary font-semibold text-sm flex-shrink-0 mt-2">
                                    {index + 1}
                                </div>

                                {/* Item Content */}
                                <div className="flex-1 space-y-3">
                                    {/* Item Text */}
                                    <input
                                        type="text"
                                        value={item.text}
                                        onChange={(e) => updateItem(index, 'text', e.target.value)}
                                        placeholder="Checklist item text..."
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent font-medium"
                                    />

                                    {/* Item Description */}
                                    <textarea
                                        value={item.description}
                                        onChange={(e) => updateItem(index, 'description', e.target.value)}
                                        placeholder="Optional description or instructions..."
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                                        rows="2"
                                    />

                                    {/* Category and Required */}
                                    <div className="flex gap-3 items-center">
                                        <div className="flex-1">
                                            <input
                                                type="text"
                                                value={item.category}
                                                onChange={(e) => updateItem(index, 'category', e.target.value)}
                                                placeholder="Category (optional)"
                                                className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                                            />
                                        </div>

                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={item.required !== false}
                                                onChange={(e) => updateItem(index, 'required', e.target.checked)}
                                                className="w-4 h-4 text-primary rounded focus:ring-2 focus:ring-primary"
                                            />
                                            <span className="text-sm font-medium text-gray-700 dark:text-slate-200">Required</span>
                                        </label>
                                    </div>

                                    {/* Item Attachments */}
                                    <div className="pt-3 border-t border-gray-200 dark:border-slate-700">
                                        <label className="text-sm font-medium text-gray-700 dark:text-slate-200 flex items-center gap-1 mb-2">
                                            <Paperclip className="w-4 h-4" />
                                            Attachments:
                                        </label>

                                        {/* Upload Button */}
                                        <div
                                            onClick={() => fileInputRefs.current[index]?.click()}
                                            className={`border-2 border-dashed rounded-lg p-2 text-center cursor-pointer transition-colors mb-2 ${uploadingItem === index
                                                ? 'border-primary/50 bg-primary/5'
                                                : 'border-gray-300 dark:border-slate-600 hover:border-primary/50 hover:bg-primary/5'
                                                }`}
                                        >
                                            {uploadingItem === index ? (
                                                <div className="flex items-center justify-center gap-2 text-primary">
                                                    <Loader className="w-4 h-4 animate-spin" />
                                                    <span className="text-xs">Uploading...</span>
                                                </div>
                                            ) : (
                                                <div className="flex items-center justify-center gap-2 text-gray-600 dark:text-slate-300">
                                                    <Upload className="w-3 h-3" />
                                                    <span className="text-xs">Click to upload</span>
                                                </div>
                                            )}
                                            <input
                                                ref={el => fileInputRefs.current[index] = el}
                                                type="file"
                                                multiple
                                                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.jpg,.jpeg,.png,.gif,.webp,.mp4,.webm"
                                                onChange={(e) => handleItemFileUpload(index, e.target.files)}
                                                className="hidden"
                                                disabled={uploadingItem === index}
                                            />
                                        </div>

                                        {/* Attached Files List */}
                                        {item.attachments && item.attachments.length > 0 && (
                                            <div className="space-y-1">
                                                {item.attachments.map((file, aIndex) => (
                                                    <div
                                                        key={aIndex}
                                                        className="flex items-center justify-between p-2 bg-gray-50 dark:bg-slate-800/50 rounded text-xs"
                                                    >
                                                        <div className="flex items-center gap-2 min-w-0">
                                                            <Paperclip className="w-3 h-3 text-gray-400 dark:text-slate-500 flex-shrink-0" />
                                                            <span className="truncate text-gray-700 dark:text-slate-200">{file.file_name}</span>
                                                        </div>
                                                        <button
                                                            type="button"
                                                            onClick={() => removeItemAttachment(index, aIndex)}
                                                            className="p-1 text-red-500 hover:bg-red-50 dark:bg-red-500/10 rounded transition-colors flex-shrink-0"
                                                        >
                                                            <X className="w-3 h-3" />
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Delete Button */}
                                <button
                                    type="button"
                                    onClick={() => deleteItem(index)}
                                    className="p-2 text-red-600 hover:bg-red-50 dark:bg-red-500/10 rounded-lg transition-colors flex-shrink-0"
                                    title="Delete Item"
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Checklist Summary */}
            {(checklist.items || []).length > 0 && (
                <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                            <span className="text-gray-600 dark:text-slate-300">Total Items:</span>
                            <span className="ml-2 font-semibold text-primary">{checklist.items.length}</span>
                        </div>
                        <div>
                            <span className="text-gray-600 dark:text-slate-300">Required:</span>
                            <span className="ml-2 font-semibold text-primary">
                                {checklist.items.filter(i => i.required !== false).length}
                            </span>
                        </div>
                        <div>
                            <span className="text-gray-600 dark:text-slate-300">Optional:</span>
                            <span className="ml-2 font-semibold text-primary">
                                {checklist.items.filter(i => i.required === false).length}
                            </span>
                        </div>
                        <div>
                            <span className="text-gray-600 dark:text-slate-300">Categories:</span>
                            <span className="ml-2 font-semibold text-primary">{categories.length}</span>
                        </div>
                    </div>

                    {/* Category Preview */}
                    {categories.length > 1 && (
                        <div className="mt-3 pt-3 border-t border-primary/20">
                            <p className="text-xs text-gray-600 dark:text-slate-300 mb-2">Categories:</p>
                            <div className="flex flex-wrap gap-2">
                                {categories.map((cat, idx) => (
                                    <span key={idx} className="px-2 py-1 bg-primary/10 text-primary rounded text-xs font-medium">
                                        {cat} ({groupedItems.find(g => g.category === cat)?.items.length})
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default ChecklistBuilder;

